// React
import React, { useState, useEffect, useRef, useContext } from 'react';

// Styles
import styles from './DetailForm.module.scss';

// Material UI
import { Button, Grid, Typography } from '@material-ui/core';
// Third party
import { trackPromise } from 'react-promise-tracker';
import { Link as RouterLink, useHistory } from 'react-router-dom';

// Contexts
import { DetailContext } from '../../../contexts/detail';

// Components
import FormMessage from '../FormMessage/FormMessage';

// Services
import {
  getDetailData as getDataTypeData,
  saveData as saveDataTypeData
} from '../../../services/data-types';

// Services
import {
  getDetailData as getTaxonomyData,
  saveData as saveTaxonomyData
} from '../../../services/taxonomies';

// Hooks
import useMounted from '../../../hooks/useMounted';
import eventBus from '../../../shared/eventBus';

export default function DetailForm() {
  // History hook
  const history = useHistory();

  // Detail Context
  const {
    typeOfData,
    title,
    itemId,
    itemHash,
    isNewItem,
    isViewOnly,
    endpoint,
    getInputs,
    baseUrl,
    getDetailId,
    getDetailHash
  } = useContext(DetailContext);

  // If the component is mounted
  const mounted = useMounted();

  // track if the form submit is success/fail
  const [messageState, setMessageState] = useState({
    open: false,
    state: '',
    messageText: '',
    messageActions: []
  });

  // Inputs
  const [inputs, setInputs] = useState(isNewItem ? getInputs() : []);

  // Keep reference to the inital passed function (we don't need to watch its changes, just need to invoke)
  const getInputsRef = useRef(getInputs);

  // Component did mount (get data)
  useEffect(() => {
    // if we are loading an existing item
    // for the first time
    if (!isNewItem) {
      let promise = null;

      if (typeOfData === 'taxonomy') {
        promise = getTaxonomyData({
          itemHash,
          endpoint,
        });
      } else {
        promise = getDataTypeData({
          itemId,
          endpoint,
        });
      }

      trackPromise(
        promise.then((newData) => {
          if (mounted.current && newData && newData.data) {
            setInputs(getInputsRef.current(newData.data));
          }
        })
      );
    }
  }, [typeOfData, itemId, itemHash, endpoint, isNewItem, mounted]);

  useEffect(() => {

    eventBus.on('showDetailFormMessage', (data_) => {

      // first we'll clear the message state
      setMessageState({
        open: false,
        state: '',
        messageText: '',
        messageActions: []
      });

      setMessageState(data_.messageState);

    });
  }, [mounted])

  // Handle form submit
  const handleSubmitDetail = (event) => {
    event.preventDefault();

    setMessageState({
      open: false,
      state: '',
      messageText: '',
      messageActions: []
    });

    const form = event.target;
    const formData = new FormData(form);
    let promise = null;

    if (typeOfData === 'taxonomy') {
      promise = saveTaxonomyData({
        formData,
        itemHash,
        endpoint,
      });
    } else {
      promise = saveDataTypeData({
        formData,
        itemId,
        endpoint,
      });
    }

    trackPromise(
      promise.then((response) => {
        let submitObj = {
          open: true
        };

        // Success
        if (response.succeeded === true) {

          // set the input values based on return data
          // mostly to update the version values
          setInputs(getInputsRef.current(response.data));

          submitObj['state'] = 'success';

          // New items (more than one) created
          if (isNewItem && Array.isArray(response.data)) {
            const counter = response.data.length;

            submitObj['messageText'] = `${counter} ${
              counter === 1 ? 'item' : 'items'
            } created!`;

            submitObj['messageText'] +=
              ' Continue editing (the first item created)?';
          }
          // One item created or edited/saved
          else {
            submitObj['messageText'] = isNewItem ? 'Created!' : 'Saved!';
            submitObj['messageText'] += ' Continue editing?';
          }

          submitObj['messageActions'] = [
            { label: 'No', clickUrl: baseUrl, style: 'secondary' },
            { label: 'Yes', dismiss: true, style: 'default' }
          ];
        }
        // Error
        else {
          submitObj['state'] = 'error';

          submitObj['messageText'] = response.message
            ? response.message
            : 'There was a problem. Please try again!';

          submitObj['messageActions'] = [{ label: 'Ok', dismiss: true }];
        }

        // Show message
        setMessageState(submitObj);

        // if we have successfully created a new item
        // route the page url to the detail page url
        if (isNewItem && response.succeeded === true) {
          const detailId = getDetailId(response.data);

          if (typeOfData === 'taxonomy') {
            const detailHash = getDetailHash(response.data);
            history.push(`${baseUrl}/${detailHash}`);
          } else {
            history.push(`${baseUrl}/${detailId}`);
          }
        }

        // if we have successfully edited an existing item
        // route the page url - only when the hash was updated
        // note: hash is created based on id+code+country, so it might change
        // during its life
        if (
          !isNewItem &&
          response.succeeded === true &&
          typeOfData === 'taxonomy'
        ) {
          const detailHash = getDetailHash(response.data);

          // new hash is different from previous one
          // so reset the page url (browser history)
          if (detailHash !== itemHash) {
            history.push(`${baseUrl}/${detailHash}`);
          }
        }
      })
    );
  };

  // JSX

  return (
    <>
      <Grid item xs={12} md="auto" className={styles["detail-header"]}>
        <Typography variant="h1" component="h1">
          {isNewItem ? 'New' : (isViewOnly ? 'View' : 'Edit')} {title}
        </Typography>
      </Grid>
      <form className={styles.form} onSubmit={handleSubmitDetail}>
        <FormMessage
          messageOpen={messageState.open}
          messageState={messageState.state}
          messageText={messageState.messageText}
          messageActions={messageState.messageActions}></FormMessage>
        {inputs &&
          inputs.map((row, i) => {
            return (
              <div key={i} className={styles.row}>
                <Grid container spacing={0} className={styles.test}>
                  {row &&
                    row.map((input, j) => {
                      if (input.hidden) {
                        return <div key={j}>{input.component}</div>;
                      }
                      return (
                        <Grid
                          className={input.className ? styles[input.className] : styles["input-wrapper"]}
                          key={j}
                          item
                          xs={input.colSize.xs}
                          md={input.colSize.md}
                          lg={input.colSize.lg}
                          xl={input.colSize.xl}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid
                              item
                              xs={input.size.xs}
                              md={input.size.md}
                              lg={input.size.lg}
                              xl={input.size.xl}>
                              {input.component}
                            </Grid>
                            {input.addNew}
                          </Grid>
                        </Grid>
                      );
                    })}
                </Grid>
              </div>
            );
          })}
        <div className={styles["actions-row"]}>
          <Grid container spacing={3}>
            <Grid item xs={12} md="auto">
              <Button
                variant="outlined"
                disableElevation={true}
                color="secondary"
                size="large"
                component={RouterLink}
                to={baseUrl}>
                Cancel
              </Button>
            </Grid>
            {
              (!isViewOnly ?
                <Grid item xs={12} md="auto">
                <Button
                  variant="contained"
                  disableElevation={true}
                  color="secondary"
                  size="large"
                  type="submit">
                  {isNewItem ? 'Create' : 'Save'}
                </Button>
              </Grid> : null)
            }
          </Grid>
        </div>
      </form>
    </>
  );
}
